import {
  Button,
  Modal,
  ModalClose,
  ModalContent,
  ModalFooter,
  ModalHeading,
} from '@idexx-laboratories/spot-react';
import classNames from 'classnames';
import React, { FunctionComponentElement, useCallback, useEffect, useRef } from 'react';
import { CustomElementProps } from '../../types/react-props-types';
import './ModalOkCancel.scss';

export interface ModalOkCancelProps extends CustomElementProps {
  id: string;
  header?: any;
  cancelCaption?: string;
  okCaption: string;
  show: boolean;
  onCancel?: () => any;
  onOk: () => any;
  onExit?: () => any;
  onClickOutside?: () => any;
  disabled?: boolean;
  okDisabled?: boolean;
  'data-testid'?: string;
  wide?: boolean;
  exitOkAction?: boolean;
  customFooter?: FunctionComponentElement<any>;
}

const ModalOkCancel = (props: ModalOkCancelProps): React.JSX.Element => {
  const {
    id,
    className,
    children,
    okCaption,
    cancelCaption,
    header,
    show,
    onCancel,
    onOk,
    onExit,
    exitOkAction,
    onClickOutside,
    okDisabled,
    disabled,
    'data-testid': dataTestId,
    customFooter,
    wide,
  } = props;

  const cancelClickedRef = useRef<boolean>(false);

  useEffect(() => {
    if (show) {
      cancelClickedRef.current = false;
    }
  }, [show]);

  const doExit = useCallback(() => {
    if (exitOkAction === true) onOk();
    else if (exitOkAction === false) onCancel?.();
    else if (!cancelClickedRef.current) onExit?.();
  }, [exitOkAction, onOk, onCancel, onExit]);

  const doCancel = useCallback(() => {
    onCancel?.();
    cancelClickedRef.current = true;
  }, [onCancel]);

  return (
    <Modal
      id={id}
      className={className}
      style={wide ? { maxWidth: '90%' } : {}}
      isOpen={show}
      onOpenChange={() => show && doExit()}
      closeOnOverlayClick={!!onClickOutside}
      closeOnEscape
      aria-describedby={undefined}
    >
      <ModalHeading title={header} />
      <ModalContent>{children}</ModalContent>
      <ModalFooter
        className={classNames({ 'hide-cancel-button': !cancelCaption })}
        cancelLink={{ text: cancelCaption, onClick: doCancel }}
      >
        {customFooter || (
          <Button
            role='modal-ok-button'
            onClick={onOk}
            disabled={okDisabled || disabled}
            data-testid={`${dataTestId}-ok-btn`}
          >
            {okCaption}
          </Button>
        )}
      </ModalFooter>
      <ModalClose />
    </Modal>
  );
};

export default ModalOkCancel;
